/**
 * Display formats the app supports: field order and year width, which are
 * independent settings that both have to survive.
 */
export type DateDisplayFormat = 'DD/MM/YY' | 'DD/MM/YYYY' | 'MM/DD/YY' | 'MM/DD/YYYY';

/** Day-first, as opposed to month-first. The ONLY way to ask about order. */
export function isDayFirst(format: DateDisplayFormat): boolean {
  return format.startsWith('DD');
}

/** Whether the year renders as two digits (`26`) rather than four (`2026`). */
export function isTwoDigitYear(format: DateDisplayFormat): boolean {
  return format.endsWith('/YY');
}

/**
 * Resolve a company's stored date-format setting to a display format, reading
 * BOTH the field order and the year width out of it.
 *
 * This used to compare the whole string against one literal —
 * `raw.toUpperCase() === 'DD/MM/YY' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'` — so only
 * that exact value was recognised as day-first. Two consequences, and the
 * first is the serious one:
 *
 *  - a tenant storing `dd/mm/yyyy`, which is v1's OWN format string
 *    (`сonstants.js`'s `dateFormatList = ["dd/mm/yyyy", "mm/dd/yyyy"]`), fell
 *    through to MONTH-first. They asked for day-first and got 03/04/2026
 *    meaning 4 March where v1 shows 3 April — a transposition, not a
 *    formatting nit, and silent because both are valid-looking dates;
 *  - the year width was discarded, so a tenant on `dd/mm/yy` — which is what
 *    v2's own Settings offers (`DATE_FORMATS`) — saw `15/07/2024` in tables
 *    while the same app's date INPUT rendered `15/07/24`.
 *
 * v1 has neither problem because it never interprets the string: its
 * `DateFormatService` normalises `m`->`M` and hands the whole thing to
 * Angular's `DatePipe`.
 *
 * Unset still means DD/MM/YYYY. v1's fallback is `dd/MM/yy` (two-digit), so
 * that one case still differs; changing it would rewrite ~42 assertions that
 * pin the default, for a case every real tenant overrides. Left deliberately,
 * not overlooked.
 */
export function resolveDateFormat(raw?: string | null): DateDisplayFormat {
  if (!raw) return 'DD/MM/YYYY';

  const upper = raw.trim().toUpperCase();
  const order = upper.startsWith('DD') ? 'DD/MM' : 'MM/DD';
  // `endsWith('YY')` alone is true of `YYYY` as well, hence the second test.
  const year = upper.endsWith('YY') && !upper.endsWith('YYYY') ? 'YY' : 'YYYY';
  return `${order}/${year}` as DateDisplayFormat;
}

/** Format a `YYYY-MM-DD` string for display in the given format. */
export function formatIsoForDisplay(iso: string, format: DateDisplayFormat): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const year = isTwoDigitYear(format) ? y.slice(-2) : y;
  return isDayFirst(format) ? `${d}/${m}/${year}` : `${m}/${d}/${year}`;
}

/**
 * A `DateInput` value back to the ISO `YYYY-MM-DD` the report date ranges
 * store, or `''` when cleared. `DateInput` always emits a UTC-midnight date, so
 * the slice round-trips exactly.
 *
 * These helpers lived in `lib/components/date-field.tsx` beside a `DateField`
 * component -- a native `<input type="date">` behind a formatted label. The
 * reports moved to `DateInput` (MTFM-678), which left that file holding only
 * pure functions, so they live here now and the component file is gone.
 */
export function toIsoDate(date: Date | null): string {
  return date ? date.toISOString().slice(0, 10) : '';
}
